import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { getUnsplashImageAPICompliance, searchUnsplashImages } from '@/lib/api/unsplash';
import { QUERY_KEYS } from '@/lib/constants';
import type { UnsplashSearchResponse } from '@/lib/api/unsplash/types';

/**
 * Infinite query hook for searching Unsplash images with pagination
 */
export function useUnsplashImages(searchQuery: string, perPage: number = 30) {
    return useInfiniteQuery<UnsplashSearchResponse, Error>({
        queryKey: [QUERY_KEYS.UNSPLASH_IMAGES, searchQuery, perPage],
        queryFn: ({ pageParam = 1 }) => {
            const query = searchQuery || 'all';
            return searchUnsplashImages(query, pageParam as number, perPage);
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            const currentPage = allPages.length;
            return lastPage.total_pages > currentPage ? currentPage + 1 : undefined;
        },
        enabled: true,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
}

/**
 * Mutation hook for triggering Unsplash download event (API compliance requirement)
 */
export function useGetUnsplashImageAPICompliance() {
    return useMutation({
        mutationKey: [QUERY_KEYS.UNSPLASH_IMAGE_API_COMPLIANCE],
        mutationFn: (imageLocationURL: string) => getUnsplashImageAPICompliance(imageLocationURL),
    });
}
